import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DocumentLibrary.css';

const DocumentLibrary = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [modules, setModules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [showLatestOnly, setShowLatestOnly] = useState(true);

  // Upload form
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    document_type: 'SOP',
    description: '',
    tags: '',
    sop_code: '',
    related_module_id: '',
    permissions: 'authenticated'
  });

  // Fetch documents
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.append('search', searchTerm);
      if (selectedType) queryParams.append('type', selectedType);
      if (selectedModule) queryParams.append('module_id', selectedModule);
      if (showLatestOnly) queryParams.append('latest', 'true');

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${apiUrl}/documents?${queryParams}`);
      const data = await response.json();

      if (data.success) {
        setDocuments(data.data);
      } else {
        setError(data.error || 'Failed to load documents');
      }
    } catch (err) {
      setError('Error loading documents: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch modules for dropdown
  const fetchModules = async () => {
    try {
      const response = await fetch(`${API_URL}/training/modules`);
      const data = await response.json();
      if (data.success) {
        setModules(data.data);
      }
    } catch (err) {
      console.error('Error loading modules:', err);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/documents/stats`);
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchModules();
    fetchStats();
  }, [searchTerm, selectedType, selectedModule, showLatestOnly]);

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({
        ...uploadForm,
        file,
        title: uploadForm.title || file.name
      });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('document_type', uploadForm.document_type);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      if (uploadForm.tags) formData.append('tags', uploadForm.tags);
      if (uploadForm.sop_code) formData.append('sop_code', uploadForm.sop_code);
      if (uploadForm.related_module_id) formData.append('related_module_id', uploadForm.related_module_id);
      formData.append('permissions', uploadForm.permissions);

      // Get auth token
      const token = localStorage.getItem('accessToken');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        // Fallback to email header for dev mode
        const email = localStorage.getItem('userEmail');
        if (email) {
          headers['x-user-email'] = email;
        }
      }

      const response = await fetch(`${API_URL}/documents/upload`, {
        method: 'POST',
        headers,
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Document uploaded successfully!');
        setShowUploadForm(false);
        setUploadForm({
          file: null,
          title: '',
          document_type: 'SOP',
          description: '',
          tags: '',
          sop_code: '',
          related_module_id: '',
          permissions: 'authenticated'
        });
        fetchDocuments();
        fetchStats();
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      setError('Error uploading document: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // Handle download
  const handleDownload = (docId, fileName) => {
    const token = localStorage.getItem('accessToken');
    const email = localStorage.getItem('userEmail');
    const headers = new Headers();
    
    if (token) {
      headers.append('Authorization', `Bearer ${token}`);
    } else if (email) {
      headers.append('x-user-email', email);
    }

    fetch(`${API_URL}/documents/${docId}/download`, {
      headers
    })
      .then(response => {
        if (!response.ok) throw new Error('Download failed');
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(err => {
        setError('Error downloading document: ' + err.message);
      });
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="document-library">
      {/* Header */}
      <div className="library-header">
        <h1>📚 Document Library</h1>
        <button onClick={() => navigate('/dashboard')} className="btn-back">
          ← Back to Dashboard
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total_documents}</div>
            <div className="stat-label">Total Documents</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.sop_count}</div>
            <div className="stat-label">SOPs</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.form_count}</div>
            <div className="stat-label">Forms</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.linked_to_modules}</div>
            <div className="stat-label">Linked to Modules</div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')} className="alert-close">×</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess('')} className="alert-close">×</button>
        </div>
      )}

      {/* Toolbar */}
      <div className="library-toolbar">
        <div className="toolbar-left">
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-select"
          >
            <option value="">All Types</option>
            <option value="SOP">SOP</option>
            <option value="Form">Form</option>
            <option value="Template">Template</option>
            <option value="Guide">Guide</option>
            <option value="Reference">Reference</option>
          </select>

          <select
            value={selectedModule}
            onChange={(e) => setSelectedModule(e.target.value)}
            className="filter-select"
          >
            <option value="">All Modules</option>
            {modules.map(mod => (
              <option key={mod.id} value={mod.id}>
                {mod.module_code} - {mod.module_name}
              </option>
            ))}
          </select>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showLatestOnly}
              onChange={(e) => setShowLatestOnly(e.target.checked)}
            />
            Latest versions only
          </label>
        </div>

        <div className="toolbar-right">
          <button 
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="btn-primary"
          >
            {showUploadForm ? '✕ Cancel' : '📤 Upload Document'}
          </button>
        </div>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="upload-form">
          <h3>Upload New Document</h3>
          <form onSubmit={handleUpload}>
            <div className="form-grid">
              <div className="form-group">
                <label>File *</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.md"
                  onChange={handleFileChange}
                  required
                />
                <small>Accepted: PDF, DOC, DOCX, XLS, XLSX, TXT, MD (max 25MB)</small>
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                  required
                  placeholder="Document title"
                />
              </div>

              <div className="form-group">
                <label>Type *</label>
                <select
                  value={uploadForm.document_type}
                  onChange={(e) => setUploadForm({...uploadForm, document_type: e.target.value})}
                  required
                >
                  <option value="SOP">SOP</option>
                  <option value="Form">Form</option>
                  <option value="Template">Template</option>
                  <option value="Guide">Guide</option>
                  <option value="Reference">Reference</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>SOP Code</label>
                <input
                  type="text"
                  value={uploadForm.sop_code}
                  onChange={(e) => setUploadForm({...uploadForm, sop_code: e.target.value})}
                  placeholder="e.g., SOP-001-02"
                />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  rows="3"
                  placeholder="Brief description of the document"
                />
              </div>

              <div className="form-group">
                <label>Tags</label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm({...uploadForm, tags: e.target.value})}
                  placeholder="Comma-separated tags"
                />
              </div>

              <div className="form-group">
                <label>Related Module</label>
                <select
                  value={uploadForm.related_module_id}
                  onChange={(e) => setUploadForm({...uploadForm, related_module_id: e.target.value})}
                >
                  <option value="">None</option>
                  {modules.map(mod => (
                    <option key={mod.id} value={mod.id}>
                      {mod.module_code} - {mod.module_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Permissions *</label>
                <select
                  value={uploadForm.permissions}
                  onChange={(e) => setUploadForm({...uploadForm, permissions: e.target.value})}
                  required
                >
                  <option value="public">Public</option>
                  <option value="authenticated">Authenticated Users</option>
                  <option value="admin">Admins Only</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={uploading}>
                {uploading ? 'Uploading...' : '📤 Upload Document'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Documents Table */}
      {loading ? (
        <div className="loading">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="empty-state">
          <p>📄 No documents found</p>
          <button onClick={() => setShowUploadForm(true)} className="btn-primary">
            Upload your first document
          </button>
        </div>
      ) : (
        <div className="documents-table-container">
          <table className="documents-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>SOP Code</th>
                <th>Module</th>
                <th>Version</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => (
                <tr key={doc.id}>
                  <td>
                    <div className="doc-title">
                      {doc.title}
                      {doc.description && (
                        <small className="doc-description">{doc.description}</small>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`badge badge-${doc.document_type.toLowerCase()}`}>
                      {doc.document_type}
                    </span>
                  </td>
                  <td>{doc.sop_code || '-'}</td>
                  <td>
                    {doc.module_code ? (
                      <span className="module-link" onClick={() => navigate(`/modules/${doc.module_code}`)}>
                        {doc.module_code}
                      </span>
                    ) : '-'}
                  </td>
                  <td>
                    <span className="version-badge">v{doc.version_number}</span>
                    {doc.is_latest_version && <span className="latest-badge">Latest</span>}
                  </td>
                  <td>{formatFileSize(doc.file_size)}</td>
                  <td>
                    <div className="upload-info">
                      {formatDate(doc.upload_date)}
                      {doc.uploaded_by_name && (
                        <small>by {doc.uploaded_by_name}</small>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleDownload(doc.id, doc.file_name)}
                        className="btn-action btn-download"
                        title="Download"
                      >
                        ⬇️
                      </button>
                      <button
                        onClick={() => navigate(`/documents/${doc.id}`)}
                        className="btn-action btn-view"
                        title="View Details"
                      >
                        👁️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DocumentLibrary;
